import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";

interface EditableCellProps {
  value: string;
  rowId: string;
  columnId: string;
  onSave: (rowId: string, columnId: string, newValue: string) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
}

export function EditableCell({ value, rowId, columnId, onSave, placeholder = "-", disabled }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    if (editValue === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(rowId, columnId, editValue);
      setEditing(false);
    } catch {
      setEditValue(value);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  if (disabled) {
    return <span className="text-sm">{value || placeholder}</span>;
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        className="h-7 px-1 py-0 text-sm"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={saving}
      />
    );
  }

  return (
    <span
      className="text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 px-1 py-0.5 rounded transition-colors"
      onClick={() => setEditing(true)}
      title="Click to edit"
    >
      {value || <span className="text-gray-400">{placeholder}</span>}
    </span>
  );
}
